"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

// Clé sessionStorage partagée avec <NotFoundTracker /> — chaîne le dernier
// path interne visité dans cet onglet, pour reconstituer le vrai referrer
// d'une navigation SPA (next/link ne déclenche jamais de vraie navigation
// HTTP, donc document.referrer ne bouge pas après le 1er hit du tab).
const SS_LAST_PATH_KEY = "sahha-last-path";

/**
 * Tracker de page view côté client.
 *
 * - Envoie un POST fire-and-forget à /api/track/pageview à chaque navigation.
 * - `keepalive: true` → la requête est garantie de partir même si l'onglet
 *   se ferme aussitôt après.
 * - Dédup par path : on ne tracke pas deux fois la même URL au sein du même
 *   render (utile pour Next 16 qui peut re-render le layout sur certains updates).
 * - Skip /admin (logique miroir de l'endpoint mais évite un round-trip pour rien).
 * - Skip pages 404 — détectées via document.title contenant "404" ou
 *   "introuvable". <NotFoundTracker /> dans not-found.tsx tracke ces hits
 *   séparément avec pageType = NOT_FOUND. Évite le double-track 404.
 *
 * Le pageType est inféré côté server à partir du path → impossible de mentir
 * depuis le navigateur.
 *
 * Referrer (fix 2026-07-21) : le tracking server-side du header HTTP Referer
 * a été retiré le 2026-06-25 (perf, cf medecin/[slug]/page.tsx) — depuis,
 * TOUTE vue vient de ce composant client. Or le header Referer d'un fetch()
 * same-origin vaut toujours l'URL de la page courante (auto-référent),
 * inexploitable. On capture donc explicitement :
 *  - 1er hit du tab → document.referrer (vrai referrer navigateur, ex. Google)
 *  - navigations SPA suivantes → dernier path interne chaîné via
 *    sessionStorage (reproduit ce que gtag.js fait déjà nativement côté GA4)
 * et on l'envoie dans le body du POST, jamais via le header HTTP.
 */
export function PageViewTracker({ locale }: { locale: string }) {
  const pathname = usePathname();
  const lastTracked = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname) return;
    if (pathname === lastTracked.current) return;
    if (pathname.includes("/admin")) return;
    lastTracked.current = pathname;

    // Petit délai pour ne pas concurrencer le first paint + laisser
    // <title> et <meta> se settle (utile pour la détection 404 ci-dessous).
    const id = window.setTimeout(() => {
      // Skip si la page courante est le 404 — sinon on double-track avec
      // <NotFoundTracker />. La détection sur document.title est fiable :
      // metadata du not-found.tsx force "404 — Page introuvable | Sahha".
      const title = typeof document !== "undefined" ? document.title : "";
      if (/(^|\s)404\b|introuvable|not\s*found/i.test(title)) return;

      let referrer: string | null = null;
      try {
        referrer = window.sessionStorage.getItem(SS_LAST_PATH_KEY) || document.referrer || null;
        window.sessionStorage.setItem(SS_LAST_PATH_KEY, pathname);
      } catch {
        // sessionStorage indisponible (navigation privée…) — referrer reste null
      }

      try {
        fetch("/api/track/pageview", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ path: pathname, locale, referrer }),
          keepalive: true,
          credentials: "same-origin",
        }).catch(() => {});
      } catch {
        // ignore
      }
    }, 350);

    return () => window.clearTimeout(id);
  }, [pathname, locale]);

  return null;
}
